import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { PlexEnvironment } from '@app/env';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { EnvName } from 'environments/env-name';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AppInsightsService } from './common/services/app-insights.service';
import { AppProjectChangeNotificationService } from './common/services/app-signal.service';
import { ToolbarComponent } from './shared/layouts/components/toolbar/toolbar.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ToolbarComponent,
    RouterOutlet,
  ],
})

export class AppComponent implements OnInit, OnDestroy {
  public auth = inject(AuthService);
  private appInsights = inject(AppInsightsService);
  private appProjectChangeNotifier = inject(AppProjectChangeNotificationService);
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private plexAuthService = inject(AuthService);
  public environment = inject(PlexEnvironment);

  private readonly _destroying$ = new Subject<void>();

  public loginDisplay = false;
  public isIframe = false;

  constructor() {
    this.appInsights.LogPageView('MainPage');
  }

  public msalInitialized = false;

  public async ngOnInit() {

    this.authService.handleRedirectObservable().subscribe();

    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

    await this.setLoginDisplay(false);
    //Handle Account Added/Removed
    this.msalBroadcastService.msalSubject$
      .subscribe(async (result: EventMessage) => {
        if (result.eventType === EventType.INITIALIZE_END) {
          this.msalInitialized = true;
          await this.setLoginDisplay(true);
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
      )
      .subscribe(async (result: EventMessage) => {
        let authenticationResult = result.payload as AuthenticationResult;
        let token = authenticationResult.accessToken;
        if (token != this.environment.accessToken) {
          this.environment.accessTokenExpiresOn = authenticationResult.expiresOn;
          if (!authenticationResult.fromCache) {
            this.environment.accessToken = token;
          } else {
            this.plexAuthService.clearRoles();
          }
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.HANDLE_REDIRECT_END),
      )
      .subscribe(async (result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          await this.setLoginDisplay(false);
        }
      });

    //Handle LOGIN -- may need to move this to a child component. Let's try this here for now
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
      )
      .subscribe(async (result: EventMessage) => {
        const payload = result.payload as AccountInfo;
        this.authService.instance.setActiveAccount(payload);
        this.environment.username = payload.username;
        this.environment.idToken = payload.idToken;

        //this sets our auth object's state to authorized, loads the user's scopes and claims
        await this.auth.authorize();
        this.appProjectChangeNotifier.appStart(this.environment);
      });

    //Handle LOGOUT -- may need to move this to a child component. Let's try this here for now
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
      )
      .subscribe(async (result: EventMessage) => {
        this.environment.accessToken = null;
        this.auth.setLoggedOut();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(async () => {
        await this.setLoginDisplay(false);
        this.checkAndSetActiveAccount();
      })

    if (this.environment.envName === EnvName.prod || this.environment.envName === EnvName.test) {
      this.appInsights.config.instrumentationKey = this.environment.appInsights.instrumentationKey;
      this.appInsights.LogPageView('Home');
    }

    document.addEventListener('dragstart', (event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        event.preventDefault();
      }
    });
  }

  async setLoginDisplay(getScopes: boolean) {
    const allAccounts = this.authService.instance.getAllAccounts();
    this.loginDisplay = allAccounts.length > 0;

    if (getScopes && this.loginDisplay && !this.auth.userAuthenticatedAndRolesLoadedAndCurrent()) {
      this.checkAndSetActiveAccount();
      await this.auth.authorize();
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}